import { useRouter, useRouterState } from '@tanstack/react-router'

/**
 * Back and close (wireframes README, FR-MAP-15): X closes to the default view; Back returns to the
 * screen this one was opened from, whichever that was. Each history entry this tab has seen is kept by
 * its index, so a screen can tell whether the entry before it was another screen (Back) or the default
 * view (X only) or nothing at all: a fresh load, a shared link, a notification. With nothing before it,
 * Back goes up to the screen's usual home (HOMES) instead, so no screen strands you.
 */
const KEY = 'fm-trail'
const DEFAULT_VIEW = new Set(['/map', '/grid'])
/** Opened from the bottom bar or the user icon: always the first screen, so never a Back. */
const ROOTS = new Set(['/wall', '/gate', '/inbox', '/you'])
/** A thread is the Inbox with it open under the tabs (bugs.md): the thread's own Back returns to the list. */
const THREAD = /^\/inbox\/[^/]+$/

/** Where a screen usually lives, for Back when it was opened from outside the app. */
const HOMES: [RegExp, string | ((path: string) => string)][] = [
  [/^\/you\/profile$/, '/you'],
  // Visibility, Notifications and Partner tools are rows at the foot of Account.
  [/^\/(you\/visibility|you\/partner|settings\/notifications)$/, '/account'],
  [/^\/you\/blocked$/, '/you/visibility'],
  // An album's home is Albums on your profile's Photos tab (`/you/albums` opens it).
  [/^\/you\/albums\/[^/]+$/, '/you/albums'],
  [/^\/you\/(reports|standing)$/, '/safety'],
  [/^\/you\/notes$/, '/plus'],
  [/^\/plus\/site$/, '/plus'],
  [/^\/you\/rules$/, '/learn'],
  [/^\/(account|invites|chain|plus|safety)$/, '/you'],
  [/^\/settings\/quick-replies$/, '/settings/notifications'],
  [/^\/account\/(data|recovery)$/, '/account'],
  [/^\/invites\/.+$/, '/invites'],
  [/^\/gate\/.+$/, '/gate'],
  [/^\/wall\/.+$/, '/wall'],
  [/^\/learn\/.+$/, '/learn'],
  [/^\/spots\/[^/]+\/edit$/, (path) => path.slice(0, -'/edit'.length)],
]
function homeOf(path: string): string | null {
  for (const [re, to] of HOMES) if (re.test(path)) return typeof to === 'string' ? to : to(path)
  return null
}

let trail: Record<number, string> = {}
try {
  trail = JSON.parse(sessionStorage.getItem(KEY) ?? '{}')
} catch {
  trail = {}
}

/** Called on every resolved navigation (router.tsx). */
export function recordTrail(index: number, pathname: string) {
  trail[index] = pathname
  try {
    sessionStorage.setItem(KEY, JSON.stringify(trail))
  } catch {
    // Private mode or blocked storage: Back still works within this page load.
  }
}

/** The Back control's action, or null when there is nothing to go back to. */
export function useBack(): (() => void) | null {
  const router = useRouter()
  const loc = useRouterState({ select: (s) => s.location })
  if (ROOTS.has(loc.pathname) || THREAD.test(loc.pathname)) return null
  const index = loc.state.__TSR_index ?? 0
  // The same screen with another tab or section open is still this screen: skip past it.
  for (let i = index - 1; i >= 0; i--) {
    const p = trail[i]
    if (p === undefined) break
    if (DEFAULT_VIEW.has(p)) return null
    if (p !== loc.pathname) return () => router.history.go(i - index)
  }
  // Nothing in the app before this screen: up to its home, in place of this entry.
  const home = homeOf(loc.pathname)
  return home ? () => router.navigate({ to: home as '/you', replace: true }) : null
}
